import React, { useState, useEffect, useRef } from 'react';
import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    TouchableOpacity,
    Dimensions,
    Image,
    ActivityIndicator,
    FlatList,
    TouchableWithoutFeedback,
    PanResponder,
    Animated,
    Pressable,
    Modal,
    TextInput,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons, FontAwesome5 } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { getAccountStats, getLeaderboard, getGamificationRules, getUserAwards, getAvailableAwards, redeemXP } from '../../api/auth';
import { Alert } from 'react-native';
import { getShadow } from '../../theme/useTheme';
import { getProfileImageUrl } from '../../utils/imageUtils';
import Skeleton from '../../components/common/Skeleton';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

// ── Badge Config ────────────────────────────────────────────────────────────
const BADGE_CONFIG = {
    Bronze: { colors: ['#92400E', '#D97706'], accent: '#F59E0B', icon: 'medal', label: 'Bronze', emoji: '🥉' },
    Silver: { colors: ['#374151', '#6B7280'], accent: '#D1D5DB', icon: 'medal', label: 'Silver', emoji: '🥈' },
    Gold: { colors: ['#78350F', '#B45309'], accent: '#FCD34D', icon: 'medal', label: 'Gold', emoji: '🥇' },
    Platinum: { colors: ['#1E1B4B', '#4338CA'], accent: '#A5B4FC', icon: 'diamond-stone', label: 'Platinum', emoji: '💎' },
};

const getBadgeCfg = (badge) => BADGE_CONFIG[badge] || BADGE_CONFIG.Bronze;

// --- Award data (Fallback if empty) ---
const DEFAULT_AWARDS = [
    { id: 'all-star', icon: 'star', title: 'Getting Started', desc: 'Complete your first task', color: '#F59E0B', unlocked: true },
];

// ── Ways to Earn UI Config (Static parts) ───────────────────────────────────
const EARN_WAYS_CONFIG = [
    { key: 'MARKETPLACE_LIST', emoji: '🛍️', label: 'Marketplace', pts: '+15', desc: 'Earn points by buying or selling items in the marketplace. Every transaction contributes to the local economy!' },
    { key: 'LOAN_REPAY', emoji: '🏦', label: 'Repay Loan', pts: '+5', desc: 'Maintain a perfect credit score by repaying your loans on or before the due date.' },
    { key: 'EVENT_JOIN', emoji: '🎫', label: 'Attend Event', pts: 'Variable', desc: 'Join workshops, youth meetups, and local events. Points earned will be as set by the event organizer.' },
    { key: 'SPACE_JOIN', emoji: '🎙️', label: 'Join Space', pts: '+5', desc: 'Participate in live mentor spaces to learn from experts and connect with peers.' },
    { key: 'INSPIRATION_WATCH', emoji: '🌅', label: 'Daily Inspiration', pts: '+1', desc: 'Watch the daily inspiration video or read quotes to stay motivated.' },
    { key: 'POST_CREATE', emoji: '📝', label: 'Create Post', pts: '+1', desc: 'Share your journey, tips, or inspiration in the social feed to help other youth grow.' },
    { key: 'POST_COMMENT', emoji: '💬', label: 'Comment', pts: '+0.5', desc: 'Engage with the community by leaving helpful and thoughtful comments on posts.' },
    { key: 'POST_LIKE', emoji: '❤️', label: 'Like', pts: '+0.5', desc: 'Spread positivity across the platform by liking content shared by your peers.' },
    { key: 'PROFILE_COMPLETE', emoji: '👤', label: 'Profile', pts: '+15', desc: 'Ensure your profile is 100% complete so mentors and partners can identify your skills.' },
];

const REQUIREMENT_LABELS = {
    'POST_COUNT': 'Posts Created',
    'POINTS_TOTAL': 'Total XP Earned',
    'MARKETPLACE_LIST_COUNT': 'Marketplace Deals',
    'POST_LIKE_COUNT': 'Likes Received',
    'POST_COMMENT_COUNT': 'Comments Made',
    'PROFILE_COMPLETE': 'Profile Completion',
};

// ── Sub-components ─────────────────────────────────────────────────────────
const AwardChip = ({ icon, title, desc, color, unlocked }) => (
    <View style={[styles.awardChip, !unlocked && styles.awardChipLocked]}>
        <View style={[styles.awardChipIcon, { backgroundColor: unlocked ? color : '#CBD5E1' }]}>
            <Ionicons name={icon} size={14} color="#FFF" />
        </View>
        <View style={{ flex: 1 }}>
            <Text style={styles.awardChipTitle}>{title}</Text>
            <Text style={styles.awardChipDesc}>{desc}</Text>
        </View>
        {unlocked
            ? <Ionicons name="checkmark-circle" size={16} color="#22C55E" />
            : <Ionicons name="lock-closed" size={13} color="#94A3B8" />
        }
    </View>
);

const StatPill = ({ icon, value, label, color, bg }) => (
    <View style={[styles.statPill, { backgroundColor: bg }]}>
        <View style={[styles.statPillIcon, { backgroundColor: color + '22' }]}>
            <Ionicons name={icon} size={13} color={color} />
        </View>
        <Text style={[styles.statPillValue, { color }]}>{value}</Text>
        <Text style={styles.statPillLabel}>{label}</Text>
    </View>
);

const PODIUM_EMOJIS = ['🥇', '🥈', '🥉'];

const LeaderRow = ({ item, index }) => {
    const isTop3 = index < 3;
    const rowCfg = getBadgeCfg(item.badgeInfo?.badge || 'Bronze');
    return (
        <View style={[styles.leaderRow, isTop3 && { borderLeftColor: ['#FBBF24', '#94A3B8', '#D97706'][index], borderLeftWidth: 3 }]}>
            <View style={styles.leaderRankWrap}>
                {isTop3 ? <Text style={styles.podiumEmoji}>{PODIUM_EMOJIS[index]}</Text> : <Text style={styles.leaderRank}>#{index + 1}</Text>}
            </View>
            <View style={styles.leaderAvatarWrap}>
                <Image source={{ uri: getProfileImageUrl(item.profileImageUrl) || 'https://i.pravatar.cc/80' }} style={styles.leaderAvatar} />
                <View style={[styles.leaderBadgeDot, { backgroundColor: rowCfg.accent }]}>
                    <Text style={styles.leaderBadgeDotEmoji}>{rowCfg.emoji}</Text>
                </View>
            </View>
            <View style={styles.leaderMeta}>
                <Text style={styles.leaderName} numberOfLines={1}>{item.name}</Text>
                <Text style={styles.leaderSub}>Lv {item.level} · {item.badgeInfo?.badge || 'Bronze'}</Text>
            </View>
            <View style={styles.leaderPtsBadge}>
                <Text style={styles.leaderPts}>{item.points}</Text>
                <Text style={styles.leaderPtsLabel}>pts</Text>
            </View>
        </View>
    );
};

const HowToEarnFooter = ({ onOpen, insets, earnWays }) => (
    <TouchableOpacity
        activeOpacity={0.9}
        onPress={onOpen}
        style={[styles.footerEarn, { paddingBottom: Math.max(insets.bottom, 12) }]}
    >
        <View style={styles.swipeHintContainer}>
            <View style={styles.swipeIndicatorPill} />
        </View>

        <View style={styles.footerEarnHeader}>
            <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1, gap: 6 }}>
                <Ionicons name="sparkles" size={14} color="#6366F1" />
                <Text style={styles.footerEarnTitle}>Ways to Earn Points</Text>
            </View>
        </View>
        <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.footerEarnScroll}
            pointerEvents="none"
        >
            {earnWays.map((item, idx) => (
                <View key={idx} style={styles.earnItem}>
                    <Text style={styles.earnEmoji}>{item.emoji}</Text>
                    <Text style={styles.earnText}>{item.label} <Text style={{ color: '#6366F1' }}>{item.pts} pts</Text></Text>
                </View>
            ))}
        </ScrollView>
    </TouchableOpacity>
);

// ── Main Screen ─────────────────────────────────────────────────────────────
const AccountProgressScreen = () => {
    const insets = useSafeAreaInsets();
    const navigation = useNavigation();
    const [activeTab, setActiveTab] = useState('progress');
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [earnWays, setEarnWays] = useState(EARN_WAYS_CONFIG);
    const [visibleCount, setVisibleCount] = useState(10);
    const [isEarnModalVisible, setIsEarnModalVisible] = useState(false);
    const [userAwards, setUserAwards] = useState([]);
    const [availableAwards, setAvailableAwards] = useState([]);
    const [redeeming, setRedeeming] = useState(false);
    const [redemptionRate, setRedemptionRate] = useState(100);
    const [redemptionMin, setRedemptionMin] = useState(1000);
    const [isRedeemModalVisible, setIsRedeemModalVisible] = useState(false);
    const [redeemInput, setRedeemInput] = useState('');

    // ── Bottom Sheet Animation & Interaction ──
    const sheetAnim = useRef(new Animated.Value(SCREEN_HEIGHT)).current;
    const backdropOpacity = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        if (isEarnModalVisible) {
            Animated.parallel([
                Animated.timing(sheetAnim, {
                    toValue: 0,
                    duration: 400,
                    useNativeDriver: true,
                }),
                Animated.timing(backdropOpacity, {
                    toValue: 1,
                    duration: 350,
                    useNativeDriver: true,
                }),
            ]).start();
        } else {
            Animated.parallel([
                Animated.timing(sheetAnim, {
                    toValue: SCREEN_HEIGHT,
                    duration: 300,
                    useNativeDriver: true,
                }),
                Animated.timing(backdropOpacity, {
                    toValue: 0,
                    duration: 250,
                    useNativeDriver: true,
                }),
            ]).start();
        }
    }, [isEarnModalVisible]);

    const panResponder = useRef(
        PanResponder.create({
            onStartShouldSetPanResponder: () => true,
            onMoveShouldSetPanResponder: (_, gestureState) => {
                return Math.abs(gestureState.dy) > 10;
            },
            onPanResponderMove: (_, gestureState) => {
                if (gestureState.dy > 0) {
                    sheetAnim.setValue(gestureState.dy);
                }
            },
            onPanResponderRelease: (_, gestureState) => {
                if (gestureState.dy > 100 || gestureState.vy > 0.5) {
                    setIsEarnModalVisible(false);
                } else {
                    Animated.spring(sheetAnim, {
                        toValue: 0,
                        useNativeDriver: true,
                        tension: 50,
                        friction: 9,
                    }).start();
                }
            },
        })
    ).current;

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [s, lb, rulesResponse, userAwardsRes, availAwardsRes] = await Promise.all([
                getAccountStats(), 
                getLeaderboard(),
                getGamificationRules(),
                getUserAwards(),
                getAvailableAwards()
            ]);
            setStats(s);
            setLeaderboard(lb);
            if (userAwardsRes.success) setUserAwards(userAwardsRes.data || []);
            if (availAwardsRes.success) setAvailableAwards(availAwardsRes.data || []);

            // Handle redemption settings and rules
            if (rulesResponse.success && rulesResponse.data) {
                const rateSetting = rulesResponse.data.find(r => r.key === 'XP_REDEMPTION_RATE');
                const minSetting = rulesResponse.data.find(r => r.key === 'XP_REDEMPTION_MIN');
                
                if (rateSetting) setRedemptionRate(parseFloat(rateSetting.value));
                if (minSetting) setRedemptionMin(parseFloat(minSetting.value));

                const updatedWays = EARN_WAYS_CONFIG.map(way => {
                    const rule = rulesResponse.data.find(r => r.key === `GAMIF_${way.key}`);
                    if (rule) {
                        return { ...way, pts: `+${rule.value}` };
                    }
                    return way;
                });
                setEarnWays(updatedWays);
            }
        } catch (e) {
            console.error('Progress fetch error:', e);
        } finally {
            setLoading(false);
        }
    };

    const handleRedeem = async () => {
        const pointsAvailable = stats?.points || 0;
        if (pointsAvailable < redemptionMin) {
            Alert.alert('Not Enough Points', `You need at least ${redemptionMin.toLocaleString()} pts to redeem for cash.`);
            return;
        }
        setRedeemInput(String(pointsAvailable));
        setIsRedeemModalVisible(true);
    };

    const confirmRedeem = async () => {
        const amount = parseInt(redeemInput);
        if (isNaN(amount) || amount < redemptionMin) {
            Alert.alert('Invalid Amount', `Minimum redemption is ${redemptionMin} XP.`);
            return;
        }
        if (amount > (stats?.points || 0)) {
            Alert.alert('Invalid Amount', 'You do not have enough XP.');
            return;
        }

        try {
            setRedeeming(true);
            const res = await redeemXP(amount);
            if (res.success) {
                setIsRedeemModalVisible(false);
                Alert.alert('Success', res.message || `Successfully redeemed ${amount} XP!`);
                fetchData();
            } else {
                Alert.alert('Error', res.error || 'Redemption failed');
            }
        } catch (err) {
            Alert.alert('Error', err.message || 'Redemption failed');
        } finally {
            setRedeeming(false);
        }
    };

    const badge = stats?.badge || 'Bronze';
    const cfg = getBadgeCfg(badge);
    const pct = Math.min(stats?.progressPercentage || 0, 100);

    if (loading) {
        return (
            <View style={[styles.container, { paddingTop: insets.top }]}>
                <View style={styles.header}><Skeleton width={30} height={30} borderRadius={15} /><Skeleton width={80} height={18} borderRadius={4} /><Skeleton width={30} height={30} borderRadius={15} /></View>
                <View style={[styles.tabBar, { backgroundColor: 'transparent' }]}><Skeleton width="48%" height={35} borderRadius={8} /><Skeleton width="48%" height={35} borderRadius={8} /></View>
                <ScrollView contentContainerStyle={styles.scrollContent}><Skeleton width="100%" height={160} borderRadius={12} style={{ marginBottom: 20 }} /><Skeleton width="100%" height={200} borderRadius={12} /></ScrollView>
            </View>
        );
    }

    return (
        <View style={[styles.container, { paddingTop: insets.top }]}>
            <View style={styles.header}>
                <TouchableOpacity onPress={() => navigation.goBack()} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}><Ionicons name="chevron-back" size={22} color="#1E293B" /></TouchableOpacity>
                <Text style={styles.headerTitle}>Progress</Text>
                <TouchableOpacity onPress={fetchData} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}><Ionicons name="refresh-outline" size={18} color="#6366F1" /></TouchableOpacity>
            </View>

            <View style={styles.tabBar}>
                {[{ key: 'progress', label: '⚡ My Stats' }, { key: 'leaderboard', label: '🏆 Leaderboard' }].map((t) => (
                    <TouchableOpacity key={t.key} style={[styles.tabPill, activeTab === t.key && styles.tabPillActive]} onPress={() => setActiveTab(t.key)}>
                        <Text style={[styles.tabPillText, activeTab === t.key && styles.tabPillTextActive]}>{t.label}</Text>
                    </TouchableOpacity>
                ))}
            </View>

            {activeTab === 'progress' ? (
                <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
                    <LinearGradient colors={cfg.colors} style={styles.levelCard} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }}>
                        <View style={styles.levelCardLeft}>
                            <View style={[styles.badgeIconWrap, { borderColor: cfg.accent + '55' }]}>
                                <Text style={styles.badgeEmoji}>{cfg.emoji}</Text>
                            </View>
                            <View style={{ marginLeft: 12 }}>
                                <Text style={styles.badgeName}>{cfg.label}</Text>
                                <View style={styles.levelNumRow}>
                                    <Text style={styles.levelNum}>Lv {stats?.level ?? 1}</Text>
                                    <View style={[styles.rankChip, { backgroundColor: cfg.accent + '33' }]}>
                                        <Text style={[styles.rankChipText, { color: cfg.accent }]}>#{stats?.level ?? 1}</Text>
                                    </View>
                                </View>
                            </View>
                        </View>
                        <View style={styles.levelCardRight}>
                            <Text style={[styles.pointsBig, { color: cfg.accent }]}>{stats?.points ?? 0}</Text>
                            <Text style={styles.pointsLabel}>total pts</Text>
                        </View>
                        <View style={styles.xpBarContainer}>
                            <View style={styles.xpBarBg}>
                                <View style={[styles.xpBarFill, { width: `${pct}%`, backgroundColor: cfg.accent }]} />
                            </View>
                            <View style={styles.xpBarLabels}>
                                <Text style={styles.xpLabel}>{stats?.points ?? 0} pts</Text>
                                <Text style={styles.xpLabel}>{pct.toFixed(1)}% — {stats?.nextLevelPoints ?? 100} pts</Text>
                            </View>
                        </View>
                    </LinearGradient>

                    {/* ── Cash Redemption Section ──────────────────────────────────────── */}
                    <View style={styles.redeemCard}>
                        <View style={styles.redeemInfo}>
                            <View style={styles.redeemIconBg}>
                                <MaterialCommunityIcons name="wallet-giftcard" size={24} color="#6366F1" />
                            </View>
                            <View style={{ flex: 1, marginLeft: 12, marginRight: 8 }}>
                                <Text style={styles.redeemTitle}>Redeem XP for Cash</Text>
                                <Text style={[styles.redeemSubtitle, { flexWrap: 'wrap' }]} numberOfLines={2}>
                                    Convert your XP to wallet cash. (Min: {redemptionMin.toLocaleString()} XP)
                                </Text>
                            </View>
                        </View>
                        <TouchableOpacity 
                            style={[styles.redeemBtn, ((stats?.points || 0) < redemptionMin || redeeming) && styles.redeemBtnDisabled]} 
                            onPress={handleRedeem}
                            disabled={(stats?.points || 0) < redemptionMin || redeeming}
                        >
                            {redeeming ? (
                                <ActivityIndicator size="small" color="#FFF" />
                            ) : (
                                <>
                                    <Text style={styles.redeemBtnText}>Redeem Now</Text>
                                    <Ionicons name="arrow-forward" size={14} color="#FFF" />
                                </>
                            )}
                        </TouchableOpacity>
                    </View>

                    <Text style={styles.sectionLabel}>Tier Roadmap</Text>
                    <View style={styles.tierRow}>
                        {[
                            { name: 'Bronze', pts: '0', emoji: '🥉', active: badge === 'Bronze' },
                            { name: 'Silver', pts: '400', emoji: '🥈', active: badge === 'Silver' },
                            { name: 'Gold', pts: '1.6k', emoji: '🥇', active: badge === 'Gold' },
                            { name: 'Platinum', pts: '3.6k', emoji: '💎', active: badge === 'Platinum' },
                        ].map((t) => (
                            <View key={t.name} style={[styles.tierBubble, t.active && styles.tierBubbleActive]}>
                                <Text style={styles.tierEmoji}>{t.emoji}</Text>
                                <Text style={[styles.tierName, t.active && { color: '#6366F1' }]}>{t.name}</Text>
                                <Text style={styles.tierPts}>{t.pts}</Text>
                            </View>
                        ))}
                    </View>

                    <Text style={styles.sectionLabel}>Activity</Text>
                    <View style={styles.statRow}>
                        <StatPill icon="document-text-outline" value={stats?.stats?.posts ?? 0} label="Posts" color="#6366F1" bg="#EEF2FF" />
                        <StatPill icon="people-outline" value={stats?.stats?.followers ?? 0} label="Followers" color="#10B981" bg="#F0FDF4" />
                        <StatPill icon="trending-up-outline" value={stats?.level ?? 1} label="Level" color="#F59E0B" bg="#FFFBEB" />
                    </View>

                    <Text style={styles.sectionLabel}>Awards</Text>
                    <View style={styles.awardsBlock}>
                        {availableAwards.length > 0 ? (
                            availableAwards.map((award) => {
                                const earned = userAwards.find(ua => ua.awardId === award.id);
                                return (
                                    <AwardChip 
                                        key={award.id} 
                                        icon={award.icon || 'star'} 
                                        title={award.title} 
                                        desc={earned ? award.description : (award.requirementType ? `Requirement: ${award.requirementValue} ${REQUIREMENT_LABELS[award.requirementType] || award.requirementType}` : award.description)} 
                                        color={award.color || '#6366F1'} 
                                        unlocked={!!earned} 
                                    />
                                );
                            })
                        ) : (
                            <Text style={styles.noAwardsText}>No awards available to earn. Stay tuned!</Text>
                        )}
                    </View>
                    <View style={{ height: 20 }} />
                </ScrollView>
            ) : (
                <FlatList
                    data={leaderboard.slice(0, visibleCount)}
                    keyExtractor={(item) => item.id.toString()}
                    renderItem={({ item, index }) => <LeaderRow item={item} index={index} />}
                    ListHeaderComponent={<View style={styles.lbHeader}><Text style={styles.lbTitle}>🏆 Rankings</Text><Text style={styles.lbSub}>Users with points only</Text></View>}
                    contentContainerStyle={styles.lbList}
                    showsVerticalScrollIndicator={false}
                />
            )}

            <HowToEarnFooter onOpen={() => setIsEarnModalVisible(true)} insets={insets} earnWays={earnWays} />

            {/* ── CUSTOM BOTTOM SHEET ── */}
            {isEarnModalVisible && (
                <View style={StyleSheet.absoluteFill} pointerEvents="box-none">
                    <Animated.View style={[styles.modalOverlay, { opacity: backdropOpacity }]}>
                        <Pressable style={StyleSheet.absoluteFill} onPress={() => setIsEarnModalVisible(false)} />
                    </Animated.View>

                    <Animated.View style={[styles.modalContent, { transform: [{ translateY: sheetAnim }] }]}>
                        <View style={styles.modalHeader} {...panResponder.panHandlers}>
                            <View style={styles.modalHandle} />
                            <View style={styles.modalTitleRow}>
                                <Text style={styles.modalTitle}>How to Earn Points</Text>
                                <TouchableOpacity onPress={() => setIsEarnModalVisible(false)} hitSlop={{ top: 15, bottom: 15, left: 15, right: 15 }}>
                                    <Ionicons name="close-circle" size={24} color="#CBD5E1" />
                                </TouchableOpacity>
                            </View>
                        </View>

                        <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={[styles.modalScroll, { paddingBottom: Math.max(insets.bottom, 40) }]}>
                            <View style={styles.modalBanner}>
                                <LinearGradient colors={['#6366F1', '#4F46E5']} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }} style={styles.modalBannerGradient}>
                                    <Ionicons name="rocket" size={32} color="#FFF" style={{ opacity: 0.9 }} />
                                    <View style={{ flex: 1, marginLeft: 12 }}>
                                        <Text style={styles.modalBannerTitle}>Level up your profile</Text>
                                        <Text style={styles.modalBannerText}>Complete tasks to earn points and unlock tiers.</Text>
                                    </View>
                                </LinearGradient>
                            </View>

                            {earnWays.map((item, index) => (
                                <View key={index} style={styles.modalEarnItem}>
                                    <View style={styles.modalEarnEmojiWrap}><Text style={styles.modalEarnEmoji}>{item.emoji}</Text></View>
                                    <View style={styles.modalEarnMeta}>
                                        <View style={styles.modalEarnLabelRow}><Text style={styles.modalEarnLabel}>{item.label}</Text><View style={styles.modalEarnPtsBadge}><Text style={styles.modalEarnPtsText}>{item.pts} pts</Text></View></View>
                                        <Text style={styles.modalEarnDesc}>{item.desc}</Text>
                                    </View>
                                </View>
                            ))}
                        </ScrollView>
                    </Animated.View>
                </View>
            )}

            {/* ── Modern Redemption Modal ── */}
            <Modal
                visible={isRedeemModalVisible}
                transparent
                animationType="fade"
                onRequestClose={() => setIsRedeemModalVisible(false)}
            >
                <TouchableWithoutFeedback onPress={() => setIsRedeemModalVisible(false)}>
                    <View style={styles.modernModalOverlay}>
                        <TouchableWithoutFeedback>
                            <View style={styles.modernModalContent}>
                                <LinearGradient
                                    colors={['#6366F1', '#4F46E5']}
                                    style={styles.modalHero}
                                    start={{ x: 0, y: 0 }}
                                    end={{ x: 1, y: 1 }}
                                >
                                    <View style={styles.modalHeroIcon}>
                                        <MaterialCommunityIcons name="wallet-giftcard" size={40} color="#6366F1" />
                                    </View>
                                    <Text style={styles.modalHeroTitle}>Redeem Points</Text>
                                    <Text style={styles.modalHeroSub}>Convert your hard-earned XP to cash</Text>
                                </LinearGradient>

                                <View style={styles.modalDetails}>
                                    <View style={styles.inputSection}>
                                        <View style={styles.inputHeader}>
                                            <Text style={styles.inputLabel}>Enter XP to Redeem</Text>
                                            <TouchableOpacity 
                                                onPress={() => setRedeemInput(String(stats?.points || 0))}
                                            >
                                                <Text style={styles.maxText}>MAX: {(stats?.points || 0).toLocaleString()}</Text>
                                            </TouchableOpacity>
                                        </View>
                                        <View style={styles.redeemInputWrap}>
                                            <TextInput 
                                                style={styles.redeemTextInput}
                                                value={redeemInput}
                                                onChangeText={setRedeemInput}
                                                keyboardType="number-pad"
                                                placeholder="0"
                                            />
                                            <Text style={styles.xpSuffix}>XP</Text>
                                        </View>
                                        <Text style={styles.minRequirementLabel}>
                                            Min required for redemption: {redemptionMin.toLocaleString()} XP
                                        </Text>
                                    </View>

                                    <View style={styles.modalStatsRow}>
                                        <View style={styles.modalStatBox}>
                                            <Text style={styles.modalStatLabel}>REDEEM</Text>
                                            <Text style={styles.modalStatValue}>{(parseInt(redeemInput) || 0).toLocaleString()} XP</Text>
                                        </View>
                                        <View style={styles.modalStatDivider} />
                                        <View style={styles.modalStatBox}>
                                            <Text style={styles.modalStatLabel}>CREDIT</Text>
                                            <Text style={[styles.modalStatValue, { color: '#10B981' }]}>
                                                Ksh {Math.floor((parseInt(redeemInput) || 0) / redemptionRate).toLocaleString()}
                                            </Text>
                                        </View>
                                    </View>

                                    <View style={styles.modalRateCard}>
                                        <Ionicons name="information-circle-outline" size={16} color="#6366F1" />
                                        <Text style={styles.modalRateText}>
                                            Current Rate: {redemptionRate} XP = Ksh 1.00
                                        </Text>
                                    </View>

                                    <View style={styles.modalActions}>
                                        <TouchableOpacity 
                                            style={styles.modalCloseBtn}
                                            onPress={() => setIsRedeemModalVisible(false)}
                                        >
                                            <Text style={styles.modalCloseBtnText}>Cancel</Text>
                                        </TouchableOpacity>
                                        <TouchableOpacity 
                                            style={[
                                                styles.modalConfirmBtn, 
                                                (redeeming || !redeemInput || parseInt(redeemInput) < redemptionMin || parseInt(redeemInput) > (stats?.points || 0)) && { opacity: 0.5 }
                                            ]}
                                            onPress={confirmRedeem}
                                            disabled={redeeming || !redeemInput || parseInt(redeemInput) < redemptionMin || parseInt(redeemInput) > (stats?.points || 0)}
                                        >
                                            {redeeming ? (
                                                <ActivityIndicator size="small" color="#FFF" />
                                            ) : (
                                                <Text style={styles.modalConfirmBtnText}>Confirm Redemption</Text>
                                            )}
                                        </TouchableOpacity>
                                    </View>
                                </View>
                            </View>
                        </TouchableWithoutFeedback>
                    </View>
                </TouchableWithoutFeedback>
            </Modal>
        </View>
    );
};

const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: '#F1F5F9' },
    header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 10 },
    headerTitle: { fontSize: 15, fontWeight: '700', color: '#1E293B' },
    tabBar: { flexDirection: 'row', marginHorizontal: 16, marginBottom: 12, backgroundColor: '#E2E8F0', borderRadius: 10, padding: 3, gap: 3 },
    tabPill: { flex: 1, paddingVertical: 7, alignItems: 'center', borderRadius: 8 },
    tabPillActive: { backgroundColor: '#FFFFFF', ...getShadow('sm') },
    tabPillText: { fontSize: 12, fontWeight: '600', color: '#64748B' },
    tabPillTextActive: { color: '#6366F1' },
    scrollContent: { paddingHorizontal: 16, paddingBottom: 20 },
    levelCard: { borderRadius: 12, padding: 16, marginBottom: 14, ...getShadow('md') },
    levelCardLeft: { flexDirection: 'row', alignItems: 'center' },
    badgeIconWrap: { width: 52, height: 52, borderRadius: 26, borderWidth: 2, backgroundColor: 'rgba(255,255,255,0.12)', alignItems: 'center', justifyContent: 'center' },
    badgeEmoji: { fontSize: 26 },
    badgeName: { fontSize: 13, fontWeight: '800', color: '#FFFFFF', textTransform: 'uppercase' },
    levelNumRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 2 },
    levelNum: { fontSize: 20, fontWeight: '900', color: '#FFFFFF' },
    rankChip: { paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4 },
    rankChipText: { fontSize: 10, fontWeight: '700' },
    levelCardRight: { alignItems: 'flex-end', marginTop: -40 },
    pointsBig: { fontSize: 28, fontWeight: '900' },
    pointsLabel: { fontSize: 9, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase' },
    xpBarContainer: { width: '100%', marginTop: 14 },
    xpBarBg: { height: 5, backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: 4, overflow: 'hidden' },
    xpBarFill: { height: '100%', borderRadius: 4 },
    xpBarLabels: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 4 },
    xpLabel: { fontSize: 9, color: 'rgba(255,255,255,0.55)', fontWeight: '600' },
    sectionLabel: { fontSize: 11, fontWeight: '700', color: '#64748B', textTransform: 'uppercase', marginBottom: 8 },
    tierRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 14, gap: 6 },
    tierBubble: { flex: 1, backgroundColor: '#FFFFFF', borderRadius: 8, paddingVertical: 8, alignItems: 'center', borderWidth: 1, borderColor: '#E2E8F0' },
    tierBubbleActive: { borderColor: '#6366F1', backgroundColor: '#EEF2FF' },
    tierEmoji: { fontSize: 18 },
    tierName: { fontSize: 9, fontWeight: '700', color: '#94A3B8', marginTop: 3 },
    tierPts: { fontSize: 8, color: '#CBD5E1', marginTop: 1 },
    statRow: { flexDirection: 'row', gap: 8, marginBottom: 14 },
    statPill: { flex: 1, borderRadius: 8, paddingVertical: 10, alignItems: 'center' },
    statPillIcon: { width: 26, height: 26, borderRadius: 6, alignItems: 'center', justifyContent: 'center', marginBottom: 2 },
    statPillValue: { fontSize: 16, fontWeight: '800' },
    statPillLabel: { fontSize: 9, color: '#94A3B8', fontWeight: '600', textTransform: 'uppercase' },
    awardsBlock: { gap: 7 },
    awardChip: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#FFFFFF', borderRadius: 8, paddingVertical: 9, paddingHorizontal: 12, gap: 10, borderWidth: 1, borderColor: '#F1F5F9' },
    awardChipLocked: { opacity: 0.55 },
    awardChipIcon: { width: 30, height: 30, borderRadius: 6, alignItems: 'center', justifyContent: 'center' },
    awardChipTitle: { fontSize: 12, fontWeight: '700', color: '#1E293B' },
    awardChipDesc: { fontSize: 10, color: '#94A3B8' },
    lbList: { paddingHorizontal: 16, paddingBottom: 40 },
    lbHeader: { marginBottom: 12 },
    lbTitle: { fontSize: 15, fontWeight: '800', color: '#1E293B' },
    lbSub: { fontSize: 11, color: '#94A3B8' },
    leaderRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#FFFFFF', paddingVertical: 9, paddingHorizontal: 12, borderRadius: 8, marginBottom: 7, borderWidth: 1, borderColor: '#F1F5F9', gap: 10 },
    leaderRankWrap: { width: 24, alignItems: 'center' },
    leaderRank: { fontSize: 11, fontWeight: '800', color: '#94A3B8' },
    leaderAvatarWrap: { position: 'relative' },
    leaderAvatar: { width: 36, height: 36, borderRadius: 18 },
    leaderBadgeDot: { width: 16, height: 16, borderRadius: 8, borderWidth: 1.5, borderColor: '#FFFFFF', position: 'absolute', bottom: -1, right: -1, alignItems: 'center', justifyContent: 'center' },
    leaderBadgeDotEmoji: { fontSize: 9, lineHeight: 12 },
    podiumEmoji: { fontSize: 18 },
    leaderMeta: { flex: 1 },
    leaderName: { fontSize: 13, fontWeight: '700', color: '#1E293B' },
    leaderSub: { fontSize: 10, color: '#94A3B8' },
    leaderPtsBadge: { alignItems: 'flex-end' },
    leaderPts: { fontSize: 14, fontWeight: '900', color: '#6366F1' },
    leaderPtsLabel: { fontSize: 9, color: '#94A3B8', textTransform: 'uppercase' },
    footerEarn: { backgroundColor: '#FFFFFF', borderTopWidth: 1, borderTopColor: '#E2E8F0', paddingVertical: 12, ...getShadow('md') },
    swipeHintContainer: { alignItems: 'center', paddingTop: 4, paddingBottom: 6 },
    swipeIndicatorPill: { width: 32, height: 4, backgroundColor: '#E2E8F0', borderRadius: 2 },
    footerEarnHeader: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, marginBottom: 8, gap: 6 },
    footerEarnTitle: { fontSize: 11, fontWeight: '800', color: '#1E293B', textTransform: 'uppercase', letterSpacing: 0.8 },
    footerEarnScroll: { paddingHorizontal: 16, gap: 10, paddingBottom: 4 },
    earnItem: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#F8FAFC', paddingHorizontal: 12, paddingVertical: 7, borderRadius: 12, borderWidth: 1, borderColor: '#F1F5F9', gap: 6 },
    earnEmoji: { fontSize: 14 },
    earnText: { fontSize: 10, fontWeight: '700', color: '#64748B' },
    modalOverlay: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(15, 23, 42, 0.4)' },
    modalContent: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#FFFFFF',
        borderTopLeftRadius: 12,
        borderTopRightRadius: 12,
        height: '82%',
        ...getShadow('xl'),
        elevation: 30,
    },
    modalBanner: { marginBottom: 20, borderRadius: 16, overflow: 'hidden' },
    modalBannerGradient: { flexDirection: 'row', alignItems: 'center', padding: 18 },
    modalBannerTitle: { color: '#FFF', fontSize: 15, fontWeight: '800' },
    modalBannerText: { color: 'rgba(255,255,255,0.8)', fontSize: 11, marginTop: 2 },
    modalHeader: { alignItems: 'center', paddingTop: 10, paddingBottom: 16 },
    modalHandle: { width: 40, height: 4, backgroundColor: '#E2E8F0', borderRadius: 2, marginBottom: 16 },
    modalTitleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%', paddingHorizontal: 24 },
    modalTitle: { fontSize: 18, fontWeight: '800', color: '#1E293B' },
    modalScroll: { paddingHorizontal: 24 },
    modalEarnItem: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 18, backgroundColor: '#F8FAFC', borderRadius: 16, padding: 16, borderWidth: 1, borderColor: '#F1F5F9', gap: 14 },
    modalEarnEmojiWrap: { width: 44, height: 44, borderRadius: 12, backgroundColor: '#FFFFFF', alignItems: 'center', justifyContent: 'center', ...getShadow('sm') },
    modalEarnEmoji: { fontSize: 22 },
    modalEarnMeta: { flex: 1 },
    modalEarnLabelRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 },
    modalEarnLabel: { fontSize: 14, fontWeight: '700', color: '#1E293B' },
    modalEarnPtsBadge: { backgroundColor: '#EEF2FF', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6 },
    modalEarnPtsText: { fontSize: 10, fontWeight: '800', color: '#6366F1' },
    modalEarnDesc: { fontSize: 11, color: '#64748B', lineHeight: 16 },
    redeemCard: {
        backgroundColor: '#FFFFFF',
        borderRadius: 16,
        padding: 16,
        marginBottom: 20,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        ...getShadow('sm'),
        borderWidth: 1,
        borderColor: '#EEF2FF'
    },
    redeemInfo: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1
    },
    redeemIconBg: {
        width: 44,
        height: 44,
        borderRadius: 12,
        backgroundColor: '#EEF2FF',
        alignItems: 'center',
        justifyContent: 'center'
    },
    redeemTitle: {
        fontSize: 15,
        fontWeight: '800',
        color: '#1E293B'
    },
    redeemSubtitle: {
        fontSize: 11,
        color: '#64748B',
        marginTop: 2,
        lineHeight: 14
    },
    redeemRateMini: {
        fontSize: 10,
        color: '#6366F1',
        fontWeight: '700',
        marginTop: 4
    },
    redeemBtn: {
        backgroundColor: '#6366F1',
        paddingHorizontal: 12,
        paddingVertical: 12,
        borderRadius: 12,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        minWidth: 100,
        justifyContent: 'center'
    },
    redeemBtnDisabled: {
        backgroundColor: '#CBD5E1'
    },
    redeemBtnText: {
        color: '#FFFFFF',
        fontSize: 12,
        fontWeight: '700'
    },
    noAwardsText: {
        fontSize: 12,
        color: '#94A3B8',
        fontStyle: 'italic',
        textAlign: 'center',
        marginTop: 4,
        marginBottom: 10
    },
    modernModalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(15, 23, 42, 0.7)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 24
    },
    modernModalContent: {
        width: '100%',
        backgroundColor: '#FFFFFF',
        borderRadius: 12,
        overflow: 'hidden',
        ...getShadow('xl')
    },
    modalHero: {
        paddingVertical: 24,
        paddingHorizontal: 16,
        alignItems: 'center',
        justifyContent: 'center'
    },
    modalHeroIcon: {
        width: 60,
        height: 60,
        borderRadius: 30,
        backgroundColor: '#FFFFFF',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 16,
        ...getShadow('md')
    },
    modalHeroTitle: {
        fontSize: 18,
        fontWeight: '900',
        color: '#FFFFFF'
    },
    modalHeroSub: {
        fontSize: 11,
        color: 'rgba(255,255,255,0.8)',
        marginTop: 4,
        fontWeight: '500'
    },
    modalDetails: {
        padding: 16
    },
    inputSection: {
        marginBottom: 16
    },
    inputHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        marginBottom: 8
    },
    inputLabel: {
        fontSize: 11,
        fontWeight: '800',
        color: '#94A3B8',
        letterSpacing: 0.5
    },
    maxText: {
        fontSize: 10,
        fontWeight: '700',
        color: '#6366F1'
    },
    redeemInputWrap: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F8FAFC',
        borderRadius: 8,
        borderWidth: 1.5,
        borderColor: '#E2E8F0',
        paddingHorizontal: 16,
        paddingVertical: 4
    },
    redeemTextInput: {
        flex: 1,
        fontSize: 20,
        fontWeight: '800',
        color: '#1E293B',
        paddingVertical: 8
    },
    xpSuffix: {
        fontSize: 16,
        fontWeight: '800',
        color: '#94A3B8',
        marginLeft: 8
    },
    modalStatsRow: {
        flexDirection: 'row',
        backgroundColor: '#F8FAFC',
        borderRadius: 8,
        padding: 12,
        borderWidth: 1,
        borderColor: '#F1F5F9',
        marginBottom: 12
    },
    modalStatBox: {
        flex: 1,
        alignItems: 'center'
    },
    modalStatDivider: {
        width: 1,
        backgroundColor: '#E2E8F0',
        marginHorizontal: 16
    },
    modalStatLabel: {
        fontSize: 9,
        fontWeight: '800',
        color: '#94A3B8',
        letterSpacing: 1,
        marginBottom: 6
    },
    modalStatValue: {
        fontSize: 16,
        fontWeight: '900',
        color: '#1E293B'
    },
    modalRateCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#EEF2FF',
        padding: 8,
        borderRadius: 6,
        gap: 8,
        marginBottom: 16
    },
    modalRateText: {
        fontSize: 12,
        fontWeight: '700',
        color: '#6366F1'
    },
    modalActions: {
        flexDirection: 'row',
        gap: 12
    },
    modalCloseBtn: {
        flex: 1,
        paddingVertical: 12,
        alignItems: 'center'
    },
    modalCloseBtnText: {
        fontSize: 14,
        fontWeight: '700',
        color: '#94A3B8'
    },
    modalConfirmBtn: {
        flex: 2,
        backgroundColor: '#6366F1',
        paddingVertical: 12,
        borderRadius: 6,
        alignItems: 'center',
        ...getShadow('md')
    },
    modalConfirmBtnText: {
        fontSize: 13,
        fontWeight: '800',
        color: '#FFFFFF'
    },
    minRequirementLabel: {
        fontSize: 10,
        color: '#94A3B8',
        marginTop: 6,
        fontWeight: '600'
    }
});

export default AccountProgressScreen;